import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Gravacao {
  id: string;
  titulo: string;
  descricao: string | null;
  audio_url: string;
  duracao: string | null;
  tamanho_bytes: number | null;
  formato: string | null;
  categoria: string;
  created_at: string;
}

interface ArquivoFila {
  id: string;
  file: File;
  titulo: string;
  categoria: string;
  status: 'pendente' | 'enviando' | 'concluido' | 'erro';
  progresso: number;
  erroMsg?: string;
}

const CATEGORIAS_PESSOAIS: Record<string, { label: string; icon: string; cor: string; bg: string }> = {
  geral:         { label: 'Geral',         icon: 'ri-mic-line',           cor: 'text-white/60',    bg: 'bg-white/10' },
  meditacao:     { label: 'Meditação',     icon: 'ri-mental-health-line', cor: 'text-rose-300',    bg: 'bg-rose-500/15' },
  pnl:           { label: 'PNL',           icon: 'ri-brain-line',         cor: 'text-violet-300',  bg: 'bg-violet-500/15' },
  gratidao:      { label: 'Gratidão',      icon: 'ri-heart-line',         cor: 'text-pink-300',    bg: 'bg-pink-500/15' },
  afirmacoes:    { label: 'Afirmações',    icon: 'ri-star-line',          cor: 'text-amber-300',   bg: 'bg-amber-500/15' },
  visualizacao:  { label: 'Visualização',  icon: 'ri-eye-line',           cor: 'text-teal-300',    bg: 'bg-teal-500/15' },
  diario:        { label: 'Diário',        icon: 'ri-book-open-line',     cor: 'text-emerald-300', bg: 'bg-emerald-500/15' },
};

const TIPOS_PERMITIDOS = [
  'audio/mpeg', 'audio/mp4', 'audio/ogg', 'audio/wav', 'audio/webm',
  'audio/aac', 'audio/x-m4a', 'audio/mp3', 'audio/flac', 'audio/x-flac',
  'audio/opus', 'audio/x-wav', 'audio/vnd.wav', 'audio/wave',
  'audio/x-aac', 'audio/3gpp', 'audio/3gpp2', 'audio/amr',
  'application/octet-stream', // OneDrive às vezes envia com esse tipo genérico
  '', // alguns navegadores não detectam o tipo
];
const EXTENSOES_PERMITIDAS = [
  '.mp3', '.mp4', '.m4a', '.ogg', '.wav', '.webm',
  '.aac', '.opus', '.flac', '.amr', '.3gp', '.3gpp', '.wma',
];

function validarArquivo(file: File): string | null {
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  const tipoOk = TIPOS_PERMITIDOS.includes(file.type);
  const extOk = EXTENSOES_PERMITIDAS.includes(ext);

  // Aceita se a extensão for válida, mesmo que o tipo MIME não seja reconhecido
  if (!tipoOk && !extOk) {
    return 'Formato não suportado. Use MP3, M4A, WAV, OGG, AAC, WEBM ou FLAC.';
  }
  if (file.size > 100 * 1024 * 1024) {
    return 'Arquivo muito grande. Máximo 100 MB.';
  }
  return null;
}

function formatarTamanho(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface AudioPlayerMiniProps {
  url: string;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
}

function AudioPlayerMini({ url, isPlaying, onPlay, onPause }: AudioPlayerMiniProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [progresso, setProgresso] = useState(0);
  const [tempoAtual, setTempoAtual] = useState('0:00');
  const [duracao, setDuracao] = useState('0:00');

  useEffect(() => {
    if (isPlaying) {
      audioRef.current?.play().catch(() => {});
    } else {
      audioRef.current?.pause();
    }
  }, [isPlaying]);

  const formatarTempo = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-3 w-full">
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={() => {
          const el = audioRef.current;
          if (!el) return;
          setTempoAtual(formatarTempo(el.currentTime));
          setProgresso(el.duration ? (el.currentTime / el.duration) * 100 : 0);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) setDuracao(formatarTempo(audioRef.current.duration));
        }}
        onEnded={() => { onPause(); setProgresso(0); setTempoAtual('0:00'); }}
      />
      <button
        onClick={isPlaying ? onPause : onPlay}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform cursor-pointer shadow-md"
      >
        <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-lg`}></i>
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer"
          onClick={(e) => {
            const el = audioRef.current;
            if (!el) return;
            const rect = e.currentTarget.getBoundingClientRect();
            el.currentTime = ((e.clientX - rect.left) / rect.width) * el.duration;
          }}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-100"
            style={{ width: `${progresso}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-white/40 text-xs font-mono">{tempoAtual}</span>
          <span className="text-white/30 text-xs font-mono">{duracao}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Componente de item da fila ───────────────────────────────────────────────
function FilaItem({
  item,
  onRemover,
  onTituloChange,
  onCategoriaChange,
}: {
  item: ArquivoFila;
  onRemover: (id: string) => void;
  onTituloChange: (id: string, titulo: string) => void;
  onCategoriaChange: (id: string, categoria: string) => void;
}) {
  const catInfo = CATEGORIAS_PESSOAIS[item.categoria] || CATEGORIAS_PESSOAIS.geral;

  return (
    <div className={`rounded-xl border p-4 transition-all ${
      item.status === 'concluido' ? 'border-emerald-500/30 bg-emerald-500/5' :
      item.status === 'erro' ? 'border-red-500/30 bg-red-500/5' :
      item.status === 'enviando' ? 'border-rose-400/30 bg-rose-500/5' :
      'border-white/10 bg-white/5'
    }`}>
      <div className="flex items-start gap-3">
        {/* Ícone de status */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${catInfo.bg}`}>
          {item.status === 'concluido' ? (
            <i className="ri-checkbox-circle-fill text-emerald-400 text-lg"></i>
          ) : item.status === 'erro' ? (
            <i className="ri-error-warning-fill text-red-400 text-lg"></i>
          ) : item.status === 'enviando' ? (
            <i className="ri-loader-4-line animate-spin text-rose-400 text-lg"></i>
          ) : (
            <i className={`${catInfo.icon} text-lg ${catInfo.cor}`}></i>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Nome do arquivo */}
          <p className="text-white/40 text-xs truncate mb-2">
            <i className="ri-file-music-line mr-1"></i>
            {item.file.name} · {formatarTamanho(item.file.size)}
          </p>

          {item.status === 'pendente' && (
            <div className="space-y-2">
              {/* Título */}
              <input
                type="text"
                value={item.titulo}
                onChange={e => onTituloChange(item.id, e.target.value)}
                placeholder="Título da gravação"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-white/25 focus:outline-none focus:border-rose-400/50 transition-colors"
              />
              {/* Categoria compacta */}
              <div className="flex gap-1.5 flex-wrap">
                {Object.entries(CATEGORIAS_PESSOAIS).map(([key, cat]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onCategoriaChange(item.id, key)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                      item.categoria === key
                        ? 'bg-rose-500/20 border border-rose-400/40 text-white'
                        : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.status === 'enviando' && (
            <div>
              <div className="flex justify-between text-xs text-white/40 mb-1">
                <span>Enviando...</span>
                <span>{item.progresso}%</span>
              </div>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-rose-500 to-pink-500 rounded-full transition-all duration-300"
                  style={{ width: `${item.progresso}%` }}
                />
              </div>
            </div>
          )}

          {item.status === 'concluido' && (
            <p className="text-emerald-400 text-xs font-medium">
              <i className="ri-checkbox-circle-line mr-1"></i>
              Enviado com sucesso!
            </p>
          )}

          {item.status === 'erro' && (
            <p className="text-red-400 text-xs">
              <i className="ri-error-warning-line mr-1"></i>
              {item.erroMsg || 'Erro ao enviar.'}
            </p>
          )}
        </div>

        {/* Botão remover */}
        {(item.status === 'pendente' || item.status === 'erro') && (
          <button
            onClick={() => onRemover(item.id)}
            className="text-white/20 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0 w-7 h-7 flex items-center justify-center"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function UploadGravacoes() {
  const [gravacoes, setGravacoes] = useState<Gravacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviandoFila, setEnviandoFila] = useState(false);
  const [audioTocando, setAudioTocando] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [fila, setFila] = useState<ArquivoFila[]>([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [deletandoId, setDeletandoId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    carregarGravacoes();
  }, []);

  const carregarGravacoes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('gravacoes_pessoais')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setGravacoes(data || []);
    } catch (err) {
      console.error('Erro ao carregar gravações:', err);
    } finally {
      setLoading(false);
    }
  };

  const adicionarArquivos = useCallback((files: FileList | File[]) => {
    setErro('');
    const novos: ArquivoFila[] = [];
    const erros: string[] = [];

    Array.from(files).forEach(file => {
      const erroValidacao = validarArquivo(file);
      if (erroValidacao) {
        erros.push(`${file.name}: ${erroValidacao}`);
        return;
      }
      novos.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        titulo: file.name.replace(/\.[^/.]+$/, ''),
        categoria: 'geral',
        status: 'pendente',
        progresso: 0,
      });
    });

    if (erros.length > 0) setErro(erros.join(' | '));
    if (novos.length > 0) {
      setFila(prev => [...prev, ...novos]);
      setShowForm(true);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      adicionarArquivos(e.target.files);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) adicionarArquivos(e.dataTransfer.files);
  }, [adicionarArquivos]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const removerDaFila = (id: string) => {
    setFila(prev => prev.filter(item => item.id !== id));
  };

  const atualizarTitulo = (id: string, titulo: string) => {
    setFila(prev => prev.map(item => item.id === id ? { ...item, titulo } : item));
  };

  const atualizarCategoria = (id: string, categoria: string) => {
    setFila(prev => prev.map(item => item.id === id ? { ...item, categoria } : item));
  };

  const atualizarStatus = (id: string, updates: Partial<ArquivoFila>) => {
    setFila(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const handleEnviarTodos = async () => {
    const pendentes = fila.filter(item => item.status === 'pendente');
    if (pendentes.length === 0) return;

    const semTitulo = pendentes.find(item => !item.titulo.trim());
    if (semTitulo) {
      setErro('Preencha o título de todas as gravações antes de enviar.');
      return;
    }

    setEnviandoFila(true);
    setErro('');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setErro('Usuário não autenticado.');
      setEnviandoFila(false);
      return;
    }

    let enviados = 0;

    for (const item of pendentes) {
      atualizarStatus(item.id, { status: 'enviando', progresso: 0 });

      try {
        const ext = item.file.name.split('.').pop()?.toLowerCase() || 'mp3';
        const nomeArquivo = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

        // Simular progresso
        const intervalo = setInterval(() => {
          atualizarStatus(item.id, { progresso: Math.min((item.progresso || 0) + 15, 80) });
        }, 200);

        const { error: uploadError } = await supabase.storage
          .from('audios')
          .upload(nomeArquivo, item.file, { upsert: false });

        clearInterval(intervalo);
        if (uploadError) throw uploadError;

        atualizarStatus(item.id, { progresso: 90 });

        const { data: urlData } = supabase.storage
          .from('audios')
          .getPublicUrl(nomeArquivo);

        const { error: dbError } = await supabase
          .from('gravacoes_pessoais')
          .insert({
            user_id: user.id,
            titulo: item.titulo.trim(),
            audio_url: urlData.publicUrl,
            tamanho_bytes: item.file.size,
            formato: ext.toUpperCase(),
            categoria: item.categoria,
          });

        if (dbError) throw dbError;

        atualizarStatus(item.id, { status: 'concluido', progresso: 100 });
        enviados++;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Erro ao enviar.';
        atualizarStatus(item.id, { status: 'erro', erroMsg: msg });
      }
    }

    setEnviandoFila(false);
    await carregarGravacoes();

    if (enviados > 0) {
      setSucesso(`${enviados} gravação${enviados !== 1 ? 'ões' : ''} enviada${enviados !== 1 ? 's' : ''} com sucesso! 🎉`);
      setTimeout(() => setSucesso(''), 5000);
    }

    // Limpar concluídos após 2s
    setTimeout(() => {
      setFila(prev => prev.filter(item => item.status !== 'concluido'));
      if (fila.every(item => item.status === 'concluido' || item.status === 'erro')) {
        const temErro = fila.some(item => item.status === 'erro');
        if (!temErro) {
          setShowForm(false);
          setFila([]);
        }
      }
    }, 2000);
  };

  const cancelarForm = () => {
    setShowForm(false);
    setFila([]);
    setErro('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pendentesCount = fila.filter(i => i.status === 'pendente').length;
  const concluidosCount = fila.filter(i => i.status === 'concluido').length;
  const totalFila = fila.length;

  const categoriasPresentes = Array.from(new Set(gravacoes.map(g => g.categoria || 'geral')));
  const gravacoesFiltradas = categoriaFiltro === 'todas'
    ? gravacoes
    : gravacoes.filter(g => (g.categoria || 'geral') === categoriaFiltro);

  return (
    <div className="mb-10">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
            <i className="ri-mic-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-white text-2xl font-bold font-['Playfair_Display']">Minhas Gravações Pessoais</h2>
            <p className="text-white/40 text-sm mt-0.5">
              {gravacoes.length} gravação{gravacoes.length !== 1 ? 'ões' : ''} enviada{gravacoes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setTimeout(() => fileInputRef.current?.click(), 100); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-cloud-2-line text-lg"></i>
            Enviar Gravações
          </button>
        )}
      </div>

      {/* Mensagem de sucesso */}
      {sucesso && (
        <div className="mb-4 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
          <i className="ri-checkbox-circle-fill text-emerald-400 text-xl"></i>
          <span className="text-emerald-300 text-sm font-medium">{sucesso}</span>
        </div>
      )}

      {/* Formulário de upload */}
      {showForm && (
        <div className="mb-6 rounded-2xl border border-white/15 bg-white/5 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
              <i className="ri-upload-cloud-2-line text-rose-400"></i>
              Enviar gravações
              {totalFila > 0 && (
                <span className="text-sm font-normal text-white/40">
                  ({concluidosCount}/{totalFila} enviado{totalFila !== 1 ? 's' : ''})
                </span>
              )}
            </h3>
            {totalFila > 0 && !enviandoFila && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white/60 rounded-lg text-xs hover:bg-white/15 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Adicionar mais
              </button>
            )}
          </div>

          {/* Área de drop */}
          <div
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !enviandoFila && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 mb-5 select-none ${
              isDragging
                ? 'border-rose-400 bg-rose-500/10 scale-[1.01]'
                : 'border-white/20 hover:border-rose-400/50 hover:bg-white/5'
            } ${enviandoFila ? 'pointer-events-none opacity-50' : ''}`}
          >
            <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center mx-auto mb-2">
              <i className={`${isDragging ? 'ri-download-cloud-2-line' : 'ri-file-music-line'} text-2xl text-rose-400`}></i>
            </div>
            <p className="text-white font-medium text-sm mb-1">
              {isDragging ? 'Solte os arquivos aqui!' : 'Arraste vários áudios ou clique para selecionar'}
            </p>
            <p className="text-white/40 text-xs">
              MP3, M4A, WAV, OGG, AAC, WEBM · Máximo 50 MB por arquivo
            </p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <span className="text-white/25 text-xs flex items-center gap-1">
                <i className="ri-stack-line"></i>
                Múltiplos arquivos de uma vez
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="*/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Fila de arquivos */}
          {fila.length > 0 && (
            <div className="space-y-3 mb-5 max-h-[420px] overflow-y-auto pr-1">
              {fila.map(item => (
                <FilaItem
                  key={item.id}
                  item={item}
                  onRemover={removerDaFila}
                  onTituloChange={atualizarTitulo}
                  onCategoriaChange={atualizarCategoria}
                />
              ))}
            </div>
          )}

          {/* Erro */}
          {erro && (
            <div className="mb-4 flex items-start gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{erro}</span>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3">
            <button
              onClick={handleEnviarTodos}
              disabled={enviandoFila || pendentesCount === 0}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enviandoFila ? (
                <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line"></i>
                  {pendentesCount > 1
                    ? `Enviar ${pendentesCount} gravações`
                    : 'Enviar gravação'}
                </>
              )}
            </button>
            <button
              onClick={cancelarForm}
              disabled={enviandoFila}
              className="px-5 py-2.5 bg-white/10 text-white/70 rounded-xl font-medium text-sm hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Filtros por categoria */}
      {!loading && gravacoes.length > 0 && categoriasPresentes.length > 1 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          <button
            onClick={() => setCategoriaFiltro('todas')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              categoriaFiltro === 'todas'
                ? 'bg-white text-gray-900 shadow'
                : 'bg-white/10 text-white/50 hover:bg-white/15 hover:text-white'
            }`}
          >
            <i className="ri-apps-line"></i>
            Todas
            <span className="ml-0.5 opacity-60">({gravacoes.length})</span>
          </button>
          {categoriasPresentes.map(cat => {
            const info = CATEGORIAS_PESSOAIS[cat] || CATEGORIAS_PESSOAIS.geral;
            const count = gravacoes.filter(g => (g.categoria || 'geral') === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setCategoriaFiltro(cat)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  categoriaFiltro === cat
                    ? 'bg-white text-gray-900 shadow'
                    : 'bg-white/10 text-white/50 hover:bg-white/15 hover:text-white'
                }`}
              >
                <i className={info.icon}></i>
                {info.label}
                <span className="ml-0.5 opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Lista de gravações */}
      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <div key={i} className="h-28 rounded-2xl bg-white/5 animate-pulse"></div>
          ))}
        </div>
      ) : gravacoes.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-mic-off-line text-3xl text-white/30"></i>
          </div>
          <p className="text-white/50 text-base">Nenhuma gravação enviada ainda.</p>
          <p className="text-white/30 text-sm mt-1">
            Salve seus áudios do WhatsApp e envie aqui para guardar suas práticas.
          </p>
          <button
            onClick={() => { setShowForm(true); setTimeout(() => fileInputRef.current?.click(), 100); }}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-cloud-2-line"></i>
            Enviar primeira gravação
          </button>
        </div>
      ) : gravacoesFiltradas.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
          <i className="ri-filter-off-line text-3xl text-white/20 mb-2 block"></i>
          <p className="text-white/40 text-sm">Nenhuma gravação nesta categoria.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {gravacoesFiltradas.map(g => {
            const catInfo = CATEGORIAS_PESSOAIS[g.categoria || 'geral'] || CATEGORIAS_PESSOAIS.geral;
            return (
              <div
                key={g.id}
                className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all duration-200 p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${catInfo.bg}`}>
                      <i className={`${catInfo.icon} text-lg ${catInfo.cor}`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <h4 className="text-white font-semibold text-sm leading-tight truncate">{g.titulo}</h4>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${catInfo.bg} ${catInfo.cor}`}>
                        {catInfo.label}
                      </span>
                      {g.descricao && (
                        <p className="text-white/40 text-xs mt-1 line-clamp-1">{g.descricao}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-white/30 text-xs">{formatarData(g.created_at)}</span>
                        {g.formato && (
                          <span className="text-xs bg-white/10 text-white/40 px-1.5 py-0.5 rounded-md">{g.formato}</span>
                        )}
                        {g.tamanho_bytes && (
                          <span className="text-white/30 text-xs">{formatarTamanho(g.tamanho_bytes)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {confirmDeleteId === g.id ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={async () => {
                          setDeletandoId(g.id);
                          try {
                            const urlParts = g.audio_url.split('/audios/');
                            if (urlParts[1]) {
                              await supabase.storage.from('audios').remove([decodeURIComponent(urlParts[1])]);
                            }
                            await supabase.from('gravacoes_pessoais').delete().eq('id', g.id);
                            setGravacoes(prev => prev.filter(x => x.id !== g.id));
                            setConfirmDeleteId(null);
                          } catch (err) {
                            console.error('Erro ao deletar:', err);
                          } finally {
                            setDeletandoId(null);
                          }
                        }}
                        disabled={deletandoId === g.id}
                        className="text-xs px-2.5 py-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {deletandoId === g.id ? <i className="ri-loader-4-line animate-spin"></i> : 'Confirmar'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs px-2.5 py-1 bg-white/10 text-white/50 rounded-lg hover:bg-white/15 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Não
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(g.id)}
                      className="text-white/20 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0 w-7 h-7 flex items-center justify-center"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  )}
                </div>

                <AudioPlayerMini
                  url={g.audio_url}
                  isPlaying={audioTocando === g.id}
                  onPlay={() => setAudioTocando(g.id)}
                  onPause={() => setAudioTocando(null)}
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 text-white/25 text-xs">
        <div className="flex items-start gap-2">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          <span>
            Para enviar áudios do WhatsApp: segure a mensagem de voz → toque em <strong className="text-white/40">Encaminhar</strong> → salve no dispositivo → clique em <strong className="text-white/40">Enviar Gravações</strong> acima.
          </span>
        </div>
        <div className="flex items-start gap-2">
          <i className="ri-cloud-line mt-0.5 flex-shrink-0 text-sky-400/40"></i>
          <span>
            <strong className="text-white/40">OneDrive / Google Drive:</strong> abra a pasta no navegador ou no aplicativo, <strong className="text-white/40">baixe o arquivo</strong> para o computador primeiro e depois arraste ou selecione aqui. Arrastar direto da nuvem pode não funcionar.
          </span>
        </div>
      </div>
    </div>
  );
}
